import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { DATE_PRESETS } from "../common/DateRangePicker";
import {
  API_DATE_FORMAT,
  PALLET_SUMMARY_LEVEL,
  PALLET_SUMMARY_PARAM,
  PalletSummaryLevel,
} from "./constants";

type ParamName = (typeof PALLET_SUMMARY_PARAM)[keyof typeof PALLET_SUMMARY_PARAM];

const API_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** A hand-edited or stale date must reach neither the picker nor the API. */
const parseDateParam = (value: string | null): Dayjs | null => {
  if (!value || !API_DATE_PATTERN.test(value)) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.startOf("day") : null;
};

const resolveLevel = (
  isBranchInScope: boolean,
  routeUuid: string | null,
): PalletSummaryLevel => {
  if (!isBranchInScope) return PALLET_SUMMARY_LEVEL.overview;
  return routeUuid ? PALLET_SUMMARY_LEVEL.route : PALLET_SUMMARY_LEVEL.branch;
};

/**
 * The level and filters of สรุปถาด, held in the query string so the back button walks back up
 * the levels and a refresh stays where it was. The level is read off which scope keys are set:
 * none is every branch, `branch_code` is one branch's routes, `route_uuid` too is one route's
 * trips.
 *
 * Only an all-branch reader names a branch. Anyone else is always inside their own branch, so a
 * `branch_code` in their URL is ignored — the server pins them whatever it says.
 */
export function usePalletSummaryScope(canPickBranch: boolean) {
  const [searchParams, setSearchParams] = useSearchParams();

  // เดือนนี้ — the first of this month to today — until a range is picked.
  const [defaultStart, defaultEnd] = useMemo(() => DATE_PRESETS.thisMonth.range(), []);
  const startParam = searchParams.get(PALLET_SUMMARY_PARAM.startDate);
  const endParam = searchParams.get(PALLET_SUMMARY_PARAM.endDate);
  // Memoized on the raw params so a Dayjs handed to the picker keeps its identity between
  // renders.
  const startDay = useMemo(
    () => parseDateParam(startParam) ?? defaultStart,
    [startParam, defaultStart],
  );
  const endDay = useMemo(
    () => parseDateParam(endParam) ?? defaultEnd,
    [endParam, defaultEnd],
  );

  const branchCode = canPickBranch
    ? searchParams.get(PALLET_SUMMARY_PARAM.branchCode) || null
    : null;
  const isBranchInScope = !canPickBranch || branchCode !== null;
  const routeUuid = isBranchInScope
    ? searchParams.get(PALLET_SUMMARY_PARAM.routeUuid) || null
    : null;

  /**
   * An empty value is deleted rather than written. Moving between levels pushes a history entry
   * so the back button returns to the level before; a new date range replaces the entry it was
   * picked on.
   */
  const updateParams = useCallback(
    (
      patch: Partial<Record<ParamName, string | null>>,
      options: { replace: boolean },
    ) => {
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        Object.entries(patch).forEach(([key, value]) => {
          if (value) next.set(key, value);
          else next.delete(key);
        });
        return next;
      }, options);
    },
    [setSearchParams],
  );

  const changeDates = useCallback(
    (start: Dayjs, end: Dayjs) =>
      updateParams(
        {
          [PALLET_SUMMARY_PARAM.startDate]: start.format(API_DATE_FORMAT),
          [PALLET_SUMMARY_PARAM.endDate]: end.format(API_DATE_FORMAT),
        },
        { replace: true },
      ),
    [updateParams],
  );

  /** A branch opens its routes; null goes back to every branch. */
  const selectBranch = useCallback(
    (nextBranchCode: string | null) =>
      updateParams(
        {
          [PALLET_SUMMARY_PARAM.branchCode]: nextBranchCode,
          [PALLET_SUMMARY_PARAM.routeUuid]: null,
        },
        { replace: false },
      ),
    [updateParams],
  );

  /** A route opens its trips; null goes back to the branch's routes. */
  const selectRoute = useCallback(
    (nextRouteUuid: string | null) =>
      updateParams(
        { [PALLET_SUMMARY_PARAM.routeUuid]: nextRouteUuid },
        { replace: false },
      ),
    [updateParams],
  );

  return {
    level: resolveLevel(isBranchInScope, routeUuid),
    startDay,
    endDay,
    branchCode,
    routeUuid,
    changeDates,
    selectBranch,
    selectRoute,
  };
}
